import { baseUrl } from '@percent/partner-dashboard/services/apiPaths'
import {
  PBPGetResponse,
  PBPListResponse,
  PBPResponse,
  PBPResponseWithError
} from '@percent/partner-dashboard/services/types/pbpResponses.types'
import {
  ConditionStateAndConditionAccountLimit,
  Employee,
  EmployeesListResponse,
  GetEmployeesListProps,
  GetVolunteeringOpportunitiesListProps,
  GetVolunteeringActivitiesListProps,
  CreateVolunteeringActivityProps,
  EditVolunteeringActivityProps,
  GetVolunteeringTimeLogsListProps,
  VolunteeringActivity,
  VolunteeringTimeLog,
  UpdateMatchingBudgetProps,
  VolunteeringOpportunity,
  VolunteeringOpportunityDetails,
  WorkplaceGivingServiceCapabilities,
  CancelOpportunityErrorCode,
  CloseOpportunityErrorCode,
  PublishOpportunityErrorCode,
  UnpublishOpportunityErrorCode,
  AssignAchievementProps,
  Achievement,
  FeatureItemProps,
  FeatureItemResponse,
  OpportunityParticipant,
  GetSlackIntegrationStatusResponse,
  GetGivingCampaignsListProps,
  GivingCampaignPartnerDto,
  GivingCampaignDto,
  GivingCampaignOverviewDto,
  GivingCampaignGroupPartnerDto,
  EmployeeAchievementsResponse,
  FeaturedItemSummary
} from './workplaceGivingService.types'

export const workplaceGivingService = ({ percentClient }: WorkplaceGivingServiceCapabilities) => ({
  getEmployeesList: (params: GetEmployeesListProps) => {
    const { filter, ...otherParams } = params

    return percentClient.get<EmployeesListResponse>(`${baseUrl}partner/giving/employees`, {
      params: {
        ...otherParams,
        ...(filter && { filter })
      }
    })
  },
  getEmployeeDetails: (employeeId: string) => {
    return percentClient.get<PBPResponse<Employee>>(`${baseUrl}partner/giving/employees/${employeeId}`)
  },
  updateMatchingBudget: ({ accountId, total }: UpdateMatchingBudgetProps) => {
    return percentClient.put<PBPResponse<ConditionStateAndConditionAccountLimit>>(
      `v1/accounts/${accountId}/donation-matching/account-limit`,
      {
        total
      }
    )
  },
  getVolunteeringOpportunitiesList: (params: GetVolunteeringOpportunitiesListProps) => {
    const { query, parentId, visibility, ...otherParams } = params

    return percentClient.get<PBPListResponse<VolunteeringOpportunity>>(`${baseUrl}partner/volunteering/opportunities`, {
      params: {
        ...otherParams,
        ...(parentId && { parentId }),
        ...(query && { query }),
        ...(visibility?.length && { visibility })
      }
    })
  },
  getVolunteeringTimeLogsList: (params: GetVolunteeringTimeLogsListProps) => {
    const { opportunityId, accountId, status, dateFrom, dateTo, type, expand, ...otherParams } = params

    return percentClient.get<PBPListResponse<VolunteeringTimeLog>>(`${baseUrl}partner/volunteering-time-logs`, {
      params: {
        ...otherParams,
        ...(opportunityId && { opportunityId }),
        ...(accountId && { accountId }),
        ...(status && { status }),
        ...(dateFrom && { dateFrom }),
        ...(dateTo && { dateTo }),
        ...(type && { type }),
        ...(expand && { expand })
      }
    })
  },
  getVolunteeringActivities: (params: GetVolunteeringActivitiesListProps) => {
    return percentClient.get<PBPListResponse<VolunteeringActivity>>(`${baseUrl}volunteering/activity-tags`, { params })
  },
  createVolunteeringActivity: (payload: CreateVolunteeringActivityProps) => {
    return percentClient.post<PBPResponse<VolunteeringActivity>>(`${baseUrl}volunteering-activities`, payload)
  },
  editVolunteeringActivity: ({ id, ...payload }: EditVolunteeringActivityProps) => {
    return percentClient.patch<PBPResponse<VolunteeringActivity>>(`${baseUrl}volunteering-activities/${id}`, payload)
  },
  getVolunteeringOpportunityDetails: (opportunityId: string) => {
    return percentClient.get<PBPResponse<VolunteeringOpportunityDetails>>(
      `${baseUrl}opportunities/${opportunityId}/details`
    )
  },
  cancelVolunteeringOpportunity: (opportunityId: string) => {
    return percentClient.post<
      PBPResponseWithError<
        { success?: boolean; error?: { code: CancelOpportunityErrorCode } },
        { code: CancelOpportunityErrorCode }
      >
    >(
      `${baseUrl}partner/volunteering/opportunities/${opportunityId}/cancel`,
      {},
      { validateStatus: status => [200, 400, 403].includes(status) }
    )
  },
  closeVolunteeringOpportunity: (opportunityId: string) => {
    return percentClient.post<
      PBPResponseWithError<
        { success?: boolean; error?: { code: CloseOpportunityErrorCode } },
        { code: CloseOpportunityErrorCode }
      >
    >(
      `${baseUrl}opportunities/${opportunityId}/close`,
      {},
      { validateStatus: status => [200, 400, 403].includes(status) }
    )
  },
  publishVolunteeringOpportunity: (opportunityId: string) => {
    return percentClient.post<
      PBPResponseWithError<
        { success?: boolean; error?: { code: PublishOpportunityErrorCode } },
        { code: PublishOpportunityErrorCode }
      >
    >(
      `${baseUrl}opportunities/${opportunityId}/publish`,
      {},
      { validateStatus: status => [200, 400, 403].includes(status) }
    )
  },
  unpublishVolunteeringOpportunity: (opportunityId: string) => {
    return percentClient.post<
      PBPResponseWithError<
        { success?: boolean; error?: { code: UnpublishOpportunityErrorCode } },
        { code: UnpublishOpportunityErrorCode }
      >
    >(
      `${baseUrl}opportunities/${opportunityId}/unpublish`,
      {},
      { validateStatus: status => [200, 400, 403].includes(status) }
    )
  },
  getAccountAchievements: ({ accountId }: { accountId: string }) => {
    return percentClient.get<PBPGetResponse<EmployeeAchievementsResponse>>(`v1/accounts/${accountId}/achievements`)
  },
  assignAchievement: ({ accountId, achievementId }: AssignAchievementProps) => {
    return percentClient.post(`v1/accounts/${accountId}/achievements`, { achievementId })
  },
  getAchievements: () => {
    return percentClient.get<PBPListResponse<Omit<Achievement, 'achievementId'>>>(`v1/achievements`)
  },
  featureItem: async ({ kind, itemId, country }: FeatureItemProps) => {
    const response = await percentClient.post<PBPResponse<FeatureItemResponse>>(`${baseUrl}partner/giving/featured`, {
      kind,
      itemId,
      ...(country.length > 0 ? { country } : {})
    })

    return response.data.data
  },
  removeFeaturedItem: async (featuredItemId: string) => {
    const response = await percentClient.delete<PBPResponse<FeatureItemResponse>>(
      `${baseUrl}partner/giving/featured/${featuredItemId}`
    )

    return response.data.data
  },
  getSlackIntegrationStatus: () => {
    return percentClient.get<PBPResponse<GetSlackIntegrationStatusResponse>>(`${baseUrl}slack/status`)
  },
  getGivingCampaignsList: (params: GetGivingCampaignsListProps) => {
    return percentClient.get<PBPListResponse<GivingCampaignPartnerDto>>(`${baseUrl}partner/giving-campaigns`, {
      params
    })
  },
  getGivingCampaign: (campaignId: string) => {
    return percentClient.get<PBPResponse<GivingCampaignDto>>(`${baseUrl}giving-campaign/${campaignId}`)
  },
  publishGivingCampaign: (campaignId: string) => {
    return percentClient.post<PBPResponse<GivingCampaignOverviewDto>>(`${baseUrl}giving-campaign/${campaignId}/publish`)
  },
  unpublishGivingCampaign: (campaignId: string) => {
    return percentClient.post<PBPResponse<GivingCampaignOverviewDto>>(
      `${baseUrl}giving-campaign/${campaignId}/unpublish`
    )
  },
  deleteGivingCampaign: (campaignId: string) => {
    return percentClient.delete<PBPResponse<GivingCampaignOverviewDto>>(`${baseUrl}giving-campaign/${campaignId}`)
  },
  deleteVolunteeringTimeLog: (id: string) => {
    return percentClient.delete<PBPResponse<VolunteeringTimeLog>>(`${baseUrl}partner/volunteering-time-logs/${id}`)
  },
  getFeaturedItemSummary: (featuredItemId: string) => {
    return percentClient.get<PBPResponse<FeaturedItemSummary>>(`${baseUrl}partner/giving/featured/${featuredItemId}`)
  },
  getCampaignGroupsList: () => {
    return percentClient.get<PBPListResponse<GivingCampaignGroupPartnerDto>>(`${baseUrl}giving-campaign-group`, {
      params: { includeSoftDeleted: true }
    })
  },
  createCampaignGroup: (payload: { name: string }) => {
    return percentClient.post<PBPResponse<GivingCampaignGroupPartnerDto>>(`${baseUrl}giving-campaign-group`, payload)
  },
  editCampaignGroup: ({ id, ...payload }: { id: string; name: string }) => {
    return percentClient.patch<PBPResponse<GivingCampaignGroupPartnerDto>>(
      `${baseUrl}giving-campaign-group/${id}`,
      payload
    )
  },
  deleteCampaignGroup: (id: string) => {
    return percentClient.delete(`${baseUrl}giving-campaign-group/${id}`)
  },
  getOpportunityParticipants: (opportunityId: string, nextUrl?: string) => {
    if (nextUrl) {
      return percentClient.get<PBPListResponse<OpportunityParticipant>>(nextUrl)
    }

    return percentClient.get<PBPListResponse<OpportunityParticipant>>(
      `v1/opportunities/${opportunityId}/participants`,
      { params: { pageSize: 100 } }
    )
  }
})
